import React, { useEffect, useState } from "react"
import { Row, Col, Modal, Label, Input } from "reactstrap"
import Select from "react-select"
import Swal from "sweetalert2"
import moment from "moment"
import { fetchNodeData } from "utils/common-fetchData"
import { colourStyles } from "utils/common-options"
import { exportStatementExcel } from "./statementExport"

const STATUS_OPTIONS = [
    { value: "all", label: "All Entries" },
    { value: "matched", label: "Matched Only" },
    { value: "unmatched", label: "Unmatched Only" },
]

// Export dialog shared by Bank Statement / Credit Card reconcile. Only the
// chosen accounts, date range and status are fetched and written to Excel.
//   endpoint     - e.g. "bankStatement/export/entries"
//   idsParam     - "bank_ids" | "card_ids"
//   accountLabel - "Bank" | "Card" (field label + Excel column header)
//   sourceKey    - "bank_name" | "card_name" (row field for that column)
const StatementExportModal = ({
    isOpen,
    toggle,
    token,
    setLoading,
    endpoint,
    idsParam,
    accountLabel,
    sourceKey,
    accountOptions,
    defaultAccount,
    typeLabels,
    filePrefix,
}) => {
    const [accounts, setAccounts] = useState([])
    const [fromDate, setFromDate] = useState("")
    const [toDate, setToDate] = useState("")
    const [status, setStatus] = useState(STATUS_OPTIONS[0])

    // Each open starts from the account on screen and the current month.
    useEffect(() => {
        if (!isOpen) return
        setAccounts(defaultAccount ? [defaultAccount] : [])
        setFromDate(moment().startOf("month").format("YYYY-MM-DD"))
        setToDate(moment().format("YYYY-MM-DD"))
        setStatus(STATUS_OPTIONS[0])
    }, [isOpen])

    const allSelected = accountOptions.length > 0 && accounts.length === accountOptions.length

    const onExport = async () => {
        if (!accounts.length) {
            Swal.fire({ icon: "warning", text: `Please select at least one ${accountLabel.toLowerCase()}.` })
            return
        }
        if (fromDate && toDate && fromDate > toDate) {
            Swal.fire({ icon: "warning", text: "From Date cannot be after To Date." })
            return
        }

        const params = new URLSearchParams({ [idsParam]: accounts.map(a => a.value).join(",") })
        if (fromDate) params.set("from", fromDate)
        if (toDate) params.set("to", toDate)
        if (status.value !== "all") params.set("status", status.value)

        const res = await fetchNodeData(setLoading, `${endpoint}?${params.toString()}`, token)
        if (!res?.success || !Array.isArray(res?.data)) {
            Swal.fire({ icon: "error", text: res?.message || "Could not export entries." })
            return
        }
        if (!res.data.length) {
            Swal.fire({ icon: "info", text: "No entries found for the selected filters." })
            return
        }

        const accountPart = allSelected
            ? `All_${accountLabel}s`
            : accounts.length === 1
                ? accounts[0].label.replace(/[^\w-]+/g, "_")
                : `${accounts.length}_${accountLabel}s`
        const fmt = d => (d ? moment(d).format("DD-MM-YYYY") : "")
        const datePart = fromDate || toDate ? `${fmt(fromDate) || "start"}_to_${fmt(toDate) || "today"}` : "All_Dates"
        const statusPart = status.value === "all" ? "" : `_${status.value}`

        exportStatementExcel({
            rows: res.data,
            typeLabels,
            sourceHeader: accountLabel,
            sourceKey,
            fileName: `${filePrefix}_${accountPart}_${datePart}${statusPart}.xlsx`,
        })
        toggle()
    }

    return (
        <Modal isOpen={isOpen} size="lg" centered toggle={toggle}>
            <div className="modal-header">
                <h5 className="modal-title mt-0">Export Entries</h5>
                <button type="button" onClick={toggle} className="btn-modal-close">
                    <span className="btn-modal-span">&times;</span>
                </button>
            </div>
            <div className="modal-body">
                <Row className="mb-3">
                    <Col md={12}>
                        <div className="d-flex justify-content-between align-items-center">
                            <Label className="mb-1">{accountLabel}s</Label>
                            <button
                                type="button"
                                className="btn btn-link btn-sm p-0"
                                onClick={() => setAccounts(allSelected ? [] : accountOptions)}
                            >
                                {allSelected ? "Clear all" : `Select all ${accountLabel.toLowerCase()}s`}
                            </button>
                        </div>
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            placeholder={`Select ${accountLabel.toLowerCase()}(s)`}
                            options={accountOptions}
                            value={accounts}
                            onChange={v => setAccounts(v || [])}
                            styles={colourStyles}
                        />
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col md={4}>
                        <Label>From Date</Label>
                        <Input type="date" value={fromDate} max={toDate || undefined} onChange={e => setFromDate(e.target.value)} />
                    </Col>
                    <Col md={4}>
                        <Label>To Date</Label>
                        <Input type="date" value={toDate} min={fromDate || undefined} onChange={e => setToDate(e.target.value)} />
                    </Col>
                    <Col md={4}>
                        <Label>Status</Label>
                        <Select options={STATUS_OPTIONS} value={status} onChange={setStatus} styles={colourStyles} />
                    </Col>
                </Row>
                <small className="text-muted">
                    Dates filter on the statement date. Leave a date empty to have no limit on that side.
                </small>
            </div>
            <div className="modal-footer">
                <button className="btn btn-red btn-label me-1" onClick={toggle}>
                    <i className="dripicons-cross me-2 icon-center label-icon"></i>
                    Cancel
                </button>
                <button className="btn btn-purple btn-label" onClick={onExport}>
                    <i className="fas fa-file-excel me-2 icon-size label-icon"></i>
                    Export
                </button>
            </div>
        </Modal>
    )
}

export default StatementExportModal
